import React, { memo, useCallback, useEffect, useState } from 'react'
import Base from '../base'
import Tab from './Tab/Tab'
import { auth, database, query, get, ref, orderByValue } from '../../firebase'
import { useAuthState } from 'react-firebase-hooks/auth'
import { useNavigate } from 'react-router-dom'
import ClipLoader from 'react-spinners/ClipLoader'
import { Helmet } from 'react-helmet'
import { getSavedPhone, removeEventData } from '../../services/database'
import Button from '../../components/Button/Button'
import CancelEventModal from '../../components/MyEvents/CancelEventModal/CancelEventModal'
import * as amplitude from '@amplitude/analytics-browser'
import moment from 'moment-timezone'
import { eventTimePassed } from '../../helpers'
import Footer from '../../components/Footer/Footer'

const MyEvents = () => {
  const [user, loading] = useAuthState(auth)
  const [loadingEvent, setLoadingEvent] = useState(false)
  const [pastEvents, setPastEvents] = useState([])
  const [futureEvents, setFutureEvents] = useState([])
  const [deleteEventModal, setDeleteEventModal] = useState(null)

  const navigate = useNavigate()

  const fetchCircles = useCallback(async () => {
    const savedPhone = getSavedPhone() ?? {}
    const userId = user?.uid ?? savedPhone.phone

    if (!userId) {
      return
    }

    setLoadingEvent(true)

    const currentDate = new Date()
    const ftEvents = []
    const psEvents = []
    let circlesObj = {}

    if (!!user) {
      circlesObj =
        (
          await get(
            query(ref(database, `circlesRef/${userId}`), orderByValue())
          )
        )?.val() || {}
    } else {
      circlesObj =
        (
          await get(
            query(ref(database, `joinedCirclesRef/${userId}`), orderByValue())
          )
        )?.val() || {}
    }

    let promises = []
    const _circles = Object.keys(circlesObj).map((key) => ({
      key,
      joinedAt: circlesObj[key]
    }))
    _circles.sort((a, b) => new Date(b.joinedAt) - new Date(a.joinedAt))
    _circles.forEach(({ key }) => {
      promises.push(get(ref(database, `circles/${key}/info`)))
    })

    const results = await Promise.all(promises)

    results.forEach((item, index) => {
      _circles[index] = { ..._circles[index], ...(item?.val() || {}) }
    })

    _circles.forEach((event) => {
      if (eventTimePassed(event)) {
        psEvents.push(event)
      } else {
        ftEvents.push(event)
      }
    })

    setPastEvents(psEvents)
    setFutureEvents(ftEvents)

    setLoadingEvent(false)
  }, [user])

  const handleCreateEvent = () => {
    amplitude.track('Create Event CTA Clicked', {
      cta_label: 'events page',
      event_type: 'digital event',
      cta_position: 'top'
    })
    removeEventData()
    navigate('/event/new')
  }

  useEffect(() => {
    if (!loading) {
      fetchCircles().then(() => null)
    }
  }, [loading, navigate, fetchCircles])

  return (
    <Base
      hideBg={true}
      allowFullHeight={true}
      mainDivClassName={'bg-[#02000B]'}
      whiteLogo
    >
      <Helmet>
        <meta charSet="utf-8" />
        <title>Coteri - See your events history</title>
        <meta
          name="description"
          content="See events you’ve hosted or attended. Click on the tiles to see the event details."
        />
      </Helmet>

      <div className="flex flex-1 flex-col md:items-stretch mt-10">
        <div className="w-full flex items-center justify-between">
          <h2 className="text-[20px] text-white">My events</h2>
          <button
            className="px-6 py-2 rounded-full text-white font-semibold border-1 border-light-purple-3 shadow-purple hover:opacity-80 transition-all duration-200 sm:visible sm:flex hidden"
            onClick={handleCreateEvent}
          >
            Create event
          </button>
        </div>

        {loadingEvent || loading ? (
          <div className="flex flex-1 items-center justify-center">
            <ClipLoader color="#5B4ABC" />
          </div>
        ) : (
          <Tab
            futureEvents={futureEvents}
            pastEvents={pastEvents}
            fetchCircles={fetchCircles}
            setDeleteEventModal={setDeleteEventModal}
          />
        )}
      </div>

      {!!deleteEventModal ? (
        <CancelEventModal
          opened
          onClose={() => setDeleteEventModal(false)}
          eventInfo={deleteEventModal}
          fetchCircles={fetchCircles}
        />
      ) : null}

      <Footer />
    </Base>
  )
}

export default memo(MyEvents)
